/* eslint-disable import/no-cycle */
/* eslint-disable max-lines */
/* eslint-disable sort-keys */
import { useEffect } from 'react';
import { atom, useRecoilState, useSetRecoilState } from 'recoil';
import { useTranslate } from '@im/react-localization';
import { useSearchParams } from 'react-router-dom';
import {
  ProgramDetailLoadResponse,
  useGetAccountNumbersList,
} from 'src/views/vendorprogram/hooks/program-detail-load';
import DefaultFormData from './DefaultFormData.json';
import {
  useValidateTemplateField,
  useVendorProgramTemplateError,
  ALPHA_ONLY_REGEX,
} from './program-template-error-handler';
import isEqual from 'lodash/isEqual';
import { useFeatureFlagEnabled } from 'src/hooks/useFeatureFlagEnabled';
import {
  IM_PRICING_GDE_UI_BIDCOSTAVGCOST_ENABLED,
  IM_PRICING_GDE_UI_MSRP_ENABLED,
  IM_PRICING_GDE_UI_GRACEPERIOD_ENABLED,
  IM_PRICING_GDE_UI_BIDATTRIBUTES_ENABLED,
  IM_PRICING_GDE_UI_CLAIMIT_DISABLED,
  IM_PRICING_GDE_UI_ORDERCOSTOVERRIDE_ENABLED,
} from 'src/util/constants';

export type Option = {
  label: React.ReactNode;
  type?: string;
  value: string | boolean;
};
export type FromFieldValue =
  | string
  | number
  | boolean
  | InclusionExclusionInfo
  | null
  | undefined;
export type FormField = {
  disabled?: boolean;
  groupId:
    | 'ProgramIdentificationAndBasicSettings'
    | 'Discount_CostAndFinancialManagement'
    | 'CustomerAndProductManagement'
    | 'Order_QuoteAndVisibilityManagement'
    | 'SystemControls_ComplianceAndAutomation'
    | 'AdditionalAttributes'
    | 'BidChangeEvents'
    | 'Review'
    | 'ChangeLog';
  multiline?: boolean;
  name?: string;
  options?: Option[];
  placeholder?: string;
  required?: boolean;
  type?:
    | 'text'
    | 'inclusionExclusion'
    | 'float'
    | 'number'
    | 'radio'
    | 'percentage';
};

export const GroupIds: FormField['groupId'][] = [
  'ProgramIdentificationAndBasicSettings',
  'Discount_CostAndFinancialManagement',
  'CustomerAndProductManagement',
  'Order_QuoteAndVisibilityManagement',
  'SystemControls_ComplianceAndAutomation',
  'AdditionalAttributes',
];

export const useGroupIds = (): FormField['groupId'][] => {
  const isBidDataEnabled = useFeatureFlagEnabled(
    IM_PRICING_GDE_UI_BIDATTRIBUTES_ENABLED
  );
  if (!isBidDataEnabled) {
    return GroupIds;
  }

  return [
    ...GroupIds.filter((groupId) => groupId !== 'AdditionalAttributes'),
    'BidChangeEvents',
    'AdditionalAttributes',
  ];
};

export type VendorProgramTemplateKeys = keyof VendorProgramTemplateProperties;

export const useVendorProgramTemplateFields = (
  isEditMode?: boolean
): Record<VendorProgramTemplateKeys, FormField> => {
  const translate = useTranslate();
  const [searchParams] = useSearchParams();
  const countryCode = searchParams.get('countryCode');
  const businessType = searchParams.get('businessType') === 'Subscription';
  const [templateInfo] = useVendorProgramTemplate();
  const isBidCostAvgCostEnabled = useFeatureFlagEnabled(
    IM_PRICING_GDE_UI_BIDCOSTAVGCOST_ENABLED
  );
  const isMsrpEnabled = useFeatureFlagEnabled(IM_PRICING_GDE_UI_MSRP_ENABLED);
  const isGracePeriodEnabled = useFeatureFlagEnabled(
    IM_PRICING_GDE_UI_GRACEPERIOD_ENABLED
  );
  const isBidDataEnabled = useFeatureFlagEnabled(
    IM_PRICING_GDE_UI_BIDATTRIBUTES_ENABLED
  );

  const isClaimItDisabled = useFeatureFlagEnabled(
    IM_PRICING_GDE_UI_CLAIMIT_DISABLED
  );
  const isOrderCostOverrideEnabled = useFeatureFlagEnabled(
    IM_PRICING_GDE_UI_ORDERCOSTOVERRIDE_ENABLED
  );
  const showBidCostAvgCostOptions =
    isBidCostAvgCostEnabled &&
    ['B', 'R'].includes(templateInfo['impulseReverseAcop'] as string);
  const flagOptions = [
    { label: translate('Common.Yes'), value: 'Y' },
    { label: translate('Common.No'), value: 'N' },
  ];
  const booleanFlagOptions = [
    { label: translate('Common.Yes'), value: true },
    { label: translate('Common.No'), value: false },
  ];

  const fields: Partial<
    Record<VendorProgramTemplateKeys, Omit<FormField, 'name'>>
  > = {
    vendorprogramsname: {
      groupId: 'ProgramIdentificationAndBasicSettings',
      required: true,
    },
    businessType: {
      groupId: 'ProgramIdentificationAndBasicSettings',
      disabled: true,
      required: true,
    },
    vendorprogramsdescription: {
      groupId: 'ProgramIdentificationAndBasicSettings',
      required: true,
      multiline: true,
    },
    vendorcode: {
      groupId: 'ProgramIdentificationAndBasicSettings',
      required: true,
      multiline: true,
    },
    source: {
      groupId: 'ProgramIdentificationAndBasicSettings',
      required: true,
    },
    companyCode: {
      groupId: 'ProgramIdentificationAndBasicSettings',
      required: true,
    },
    currencyGlobal: {
      groupId: 'ProgramIdentificationAndBasicSettings',
      required: true,
    },
    ...(!businessType && {
      impulseProgramName: { groupId: 'ProgramIdentificationAndBasicSettings' },
      impulsePROMOFLAG: { groupId: 'ProgramIdentificationAndBasicSettings' },
    }),
    ...(!isClaimItDisabled
      ? {
          bidType: {
            groupId: 'ProgramIdentificationAndBasicSettings',
            required: true,
            options: [
              { label: 'Select', value: '' },
              {
                label: translate('VendorProgram.VendorClaimBid'),
                value: 'REBATE',
              },
              {
                label: translate('VendorProgram.MixedBid'),
                value: 'MIXED',
              },
              {
                label: translate('VendorProgram.IMFundedBid'),
                value: 'IMFUNDED',
              },
              {
                label: translate('VendorProgram.VendorFundedBid'),
                value: 'VENDORFUNDED',
              },
              {
                label: translate('VendorProgram.UpfrontBid'),
                value: 'UPFRONT',
              },
            ],
          },
          claimAccountNumberKey: {
            groupId: 'ProgramIdentificationAndBasicSettings',
            required: templateInfo['bidType'] === 'IMFUNDED',
            options: [{ label: '', value: '' }],
          },
          claimName: {
            groupId: 'ProgramIdentificationAndBasicSettings',
          },
        }
      : null),
    vendorACOPDiscountType: {
      groupId: 'Discount_CostAndFinancialManagement',
      options: [
        'BranchDiscount',
        'ResellerDiscount',
        'ProgramDiscount',
        'CompanyWideDiscount',
        'ResellerEndUserDiscount',
      ].map((o) => ({ label: o, value: o })),
      required: true,
    },
    discountType: {
      groupId: 'Discount_CostAndFinancialManagement',
      options: [
        'so_cost',
        'delta',
        'pct_off_repl',
        'pct_of_list',
        'pct_off_list',
        'bdf',
        'pct_off_avg',
        'pct_off_base_cost',
        'pct_off_type_c',
        'pct_off_type_d',
      ]
        .sort(undefined)
        .map((o) => ({ label: o, value: o })),
      required: true,
    },
    costBasis: {
      groupId: 'Discount_CostAndFinancialManagement',
      required: true,
      options: costBasisOptions[
        countryCode as keyof typeof costBasisOptions
      ]?.map((o) => ({ label: o, value: o })),
    },
    costBasisOverride: {
      groupId: 'Discount_CostAndFinancialManagement',
      options: costBasisOverrideOptions[
        countryCode as keyof typeof costBasisOverrideOptions
      ]?.map((o) => ({
        label: o.toLocaleLowerCase(),
        value: o,
      })),
    },
    ...(!businessType && {
      acopTypeOverride: {
        groupId: 'Discount_CostAndFinancialManagement',
        options: [
          { label: translate('Common.No'), value: 'NO' },
          {
            label: translate('VendorProgram.BidParsingDeltaName'),
            value: 'DELTA',
          },
        ],
        type: 'radio',
      },
    }),
    selectableDiscountLogic: {
      groupId: 'Discount_CostAndFinancialManagement',
      options: [
        { label: 'Y', value: 'Y' },
        { label: 'N', value: 'N' },
        { label: 'S', value: 'S' },
        { label: 'O', value: 'O' },
      ],
    },
    ...(!businessType && {
      fundUsageAmount: { groupId: 'Discount_CostAndFinancialManagement' },
      impulseDebitEnOPercentage: {
        groupId: 'Discount_CostAndFinancialManagement',
        type: 'percentage',
      },
      impulseDebitMDFPercentage: {
        groupId: 'Discount_CostAndFinancialManagement',
        type: 'percentage',
      },
      impulseDebitOTHPercentage: {
        groupId: 'Discount_CostAndFinancialManagement',
        type: 'percentage',
      },
      impulseDebitVRFPPercentage: {
        groupId: 'Discount_CostAndFinancialManagement',
        type: 'percentage',
      },
      impulseDebitVRFVPercentage: {
        groupId: 'Discount_CostAndFinancialManagement',
        type: 'percentage',
      },
      impulseDebitVendorPercentage: {
        groupId: 'Discount_CostAndFinancialManagement',
        type: 'percentage',
      },
      poccFlagging: {
        groupId: 'Discount_CostAndFinancialManagement',
        options: flagOptions,
        type: 'radio',
      },
      impulseReverseAcop: {
        groupId: 'Discount_CostAndFinancialManagement',
        options: [
          { label: 'Y', value: 'Y' },
          { label: 'N', value: 'N' },
          ...(isBidCostAvgCostEnabled
            ? [
                { label: 'B', value: 'B' },
                { label: 'R', value: 'R' },
              ]
            : []),
        ],
      },
      averageCostValidation: {
        groupId: 'Discount_CostAndFinancialManagement',
        options: [
          ...flagOptions,
          ...(showBidCostAvgCostOptions
            ? [
                { label: 'No - increase cost', value: 'No_increase_cost' },
                { label: 'No - zero delta', value: 'No_zero_delta' },
              ]
            : []),
        ],
      },
      costAdjustmentType: {
        groupId: 'Discount_CostAndFinancialManagement',
        options: [
          { label: translate('VendorProgram.None'), value: 'NONE' },
          { label: 'additional_cost', value: 'ADDITIONAL_COST' },
          { label: 'on_cost', value: 'ON_COST' },
        ],
      },
      ingramCostBasis: {
        groupId: 'Discount_CostAndFinancialManagement',
        options: [
          { label: 'delta', value: 'DELTA' },
          { label: 'so_cost_plus', value: 'SO_COST_PLUS' },
        ],
      },
    }),
    ...(isMsrpEnabled && {
      msrpSource: {
        groupId: 'Discount_CostAndFinancialManagement',
        options: (isMsrpSystemOnlyDiscountType(templateInfo['discountType'])
          ? ['System']
          : ['Bid', 'System']
        ).map((o) => ({ label: o, value: o })),
      },
    }),
    ...(isGracePeriodEnabled && {
      acopGracePeriod: {
        groupId: 'Discount_CostAndFinancialManagement',
        type: 'number',
      },
    }),
    customergroupinfo: {
      groupId: 'CustomerAndProductManagement',
      type: 'inclusionExclusion',
      options: ['inclusion', 'exclusion'].map((o) => ({ label: o, value: o })),
    },
    isMultiReseller: {
      groupId: 'CustomerAndProductManagement',
      options: [
        ...flagOptions,
        { label: translate('VendorProgram.AcrossVersion'), value: 'AV' },
      ],
    },
    ...(!businessType && {
      sharedQuantityflag: {
        groupId: 'CustomerAndProductManagement',
        options: flagOptions,
        type: 'radio',
      },
      customerIdGenerated: {
        groupId: 'CustomerAndProductManagement',
        options: flagOptions,
        type: 'radio',
      },
    }),
    isExactEnduserNameMatchRequired: {
      groupId: 'CustomerAndProductManagement',
      options: flagOptions,
      type: 'radio',
    },
    uniqueEnduserResellerForClaims: {
      groupId: 'CustomerAndProductManagement',
      options: booleanFlagOptions,
      type: 'radio',
    },
    endUserAddressMatchPercentage: {
      groupId: 'CustomerAndProductManagement',
      type: 'percentage',
    },
    isMapAllIMSku: {
      groupId: 'CustomerAndProductManagement',
      options: booleanFlagOptions,
      type: 'radio',
    },
    visibleonWeb: {
      groupId: 'Order_QuoteAndVisibilityManagement',
      options: flagOptions,
      type: 'radio',
    },
    autoQuote: {
      groupId: 'Order_QuoteAndVisibilityManagement',
      options: flagOptions,
      type: 'radio',
    },
    enableCustomOnDemandQuote: {
      groupId: 'Order_QuoteAndVisibilityManagement',
      options: flagOptions,
      type: 'radio',
    },
    isPartialOrderingAllowed: {
      groupId: 'Order_QuoteAndVisibilityManagement',
      options: booleanFlagOptions,
      type: 'radio',
    },
    enableQuoteQuantityEdit: {
      groupId: 'Order_QuoteAndVisibilityManagement',
      options: flagOptions,
      type: 'radio',
    },
    skipAutoQuoteForSameVersion: {
      groupId: 'Order_QuoteAndVisibilityManagement',
      options: booleanFlagOptions,
      type: 'radio',
    },
    thresholdCount: {
      groupId: 'Order_QuoteAndVisibilityManagement',
      type: 'number',
    },
    suggestedResellerPriceRequired: {
      groupId: 'Order_QuoteAndVisibilityManagement',
      options: booleanFlagOptions,
      type: 'radio',
    },
    isSplitAndRemoveAllowed: {
      groupId: 'Order_QuoteAndVisibilityManagement',
      options: booleanFlagOptions,
      type: 'radio',
    },
    isDealSplitAllowedByVendorDiscountType: {
      groupId: 'Order_QuoteAndVisibilityManagement',
      options: booleanFlagOptions,
      type: 'radio',
    },
    ...(!businessType && {
      orderReCostRePrice: {
        groupId: 'Order_QuoteAndVisibilityManagement',
        options: flagOptions,
        type: 'radio',
      },
    }),
    orderType: {
      groupId: 'Order_QuoteAndVisibilityManagement',
      options: [
        { label: 'Direct Ship', value: 'D' },
        { label: 'Open', value: 'O' },
        { label: 'Stocking', value: 'S' },
        { label: 'None', value: 'N' },
      ],
    },
    discountVisible: {
      groupId: 'Order_QuoteAndVisibilityManagement',
      options: flagOptions,
      type: 'radio',
    },
    ...(!businessType && {
      impulseAcop: {
        groupId: 'Order_QuoteAndVisibilityManagement',
        options: [
          {
            label: translate('VendorProgram.OrderEntry'),
            value: 'OrderEntry',
          },
          { label: translate('VendorProgram.None'), value: 'None' },
        ],
      },
      euMandatoryForClaims: {
        groupId: 'Order_QuoteAndVisibilityManagement',
        options: [
          ...flagOptions,
          { label: translate('VendorProgram.RenameReplaceFlag'), value: 'R' },
        ],
      },
      glAccount: { groupId: 'Order_QuoteAndVisibilityManagement' },
      productLocalizationForWeb: {
        groupId: 'Order_QuoteAndVisibilityManagement',
        options: [
          ...flagOptions,
          {
            label: translate('VendorProgram.YesAllLocalizations'),
            value: 'YesL10N',
          },
        ],
      },
      ...(isOrderCostOverrideEnabled && {
        orderCostOverride: {
          groupId: 'Order_QuoteAndVisibilityManagement',
          options: booleanFlagOptions,
          type: 'radio',
        },
      }),
      completeShipment: {
        groupId: 'Order_QuoteAndVisibilityManagement',
        options: booleanFlagOptions,
        type: 'radio',
      },
    }),

    ...(!businessType && {
      vendorDiscountHold: {
        groupId: 'SystemControls_ComplianceAndAutomation',
        options: flagOptions,
        type: 'radio',
      },
      impulseFaxFlag: { groupId: 'SystemControls_ComplianceAndAutomation' },
      impulseHardCopyFlag: {
        groupId: 'SystemControls_ComplianceAndAutomation',
      },
      impulseMinimumAcopType: {
        groupId: 'SystemControls_ComplianceAndAutomation',
      },
      impulseCompetitor: { groupId: 'SystemControls_ComplianceAndAutomation' },
    }),
    coG_Approval: {
      groupId: 'SystemControls_ComplianceAndAutomation',
      options: flagOptions,
      type: 'radio',
    },
    bidRequired: {
      groupId: 'SystemControls_ComplianceAndAutomation',
      options: flagOptions,
      type: 'radio',
    },
    nonorderlevel: {
      groupId: 'SystemControls_ComplianceAndAutomation',
      options: flagOptions,
      type: 'radio',
    },
    versionNumberGenerated: {
      groupId: 'SystemControls_ComplianceAndAutomation',
      options: flagOptions,
      type: 'radio',
    },
    quantityTracking: {
      groupId: 'SystemControls_ComplianceAndAutomation',
      options: [
        ...flagOptions,
        {
          label: translate('VendorProgram.RemainingQty'),
          value: 'R',
        },
        {
          label: translate('VendorProgram.TrackQtyWithinVersion'),
          value: 'WIV',
        },
      ],
    },
    bcnLinkingLevel: {
      groupId: 'SystemControls_ComplianceAndAutomation',
      options: ['Program', 'Deal'].map((o) => ({ label: o, value: o })),
    },
    ...(!businessType && {
      resellerAuthorization: {
        groupId: 'SystemControls_ComplianceAndAutomation',
        options: flagOptions,
        type: 'radio',
      },
    }),
    isProgramEligibleForFreeProducts: {
      groupId: 'SystemControls_ComplianceAndAutomation',
      options: booleanFlagOptions,
      type: 'radio',
    },
    enableRulesEngine: {
      groupId: 'SystemControls_ComplianceAndAutomation',
      options: booleanFlagOptions,
      type: 'radio',
    },
    enableSequencing: {
      groupId: 'SystemControls_ComplianceAndAutomation',
      options: booleanFlagOptions,
      type: 'radio',
    },
    expiringSoon: {
      groupId: 'SystemControls_ComplianceAndAutomation',
      type: 'number',
    },
    enableProductFamilyDiscounts: {
      groupId: 'SystemControls_ComplianceAndAutomation',
      options: booleanFlagOptions,
      type: 'radio',
    },

    ...(!businessType && {
      impulseCommentLine1: { groupId: 'AdditionalAttributes' },
      impulseCommentLine2: { groupId: 'AdditionalAttributes' },
      impulsePrintCommentsFlag: {
        groupId: 'AdditionalAttributes',
        options: flagOptions,
        type: 'radio',
      },
      impulseComponentFlag: {
        groupId: 'AdditionalAttributes',
        options: [
          { label: 'Y', value: 'Y' },
          { label: 'N', value: 'N' },
          { label: 'S', value: 'S' },
          { label: 'X', value: 'X' },
        ],
      },
    }),

    ...(isBidDataEnabled && {
      enableExpiryDateAttributeChange: {
        groupId: 'BidChangeEvents',
        options: booleanFlagOptions,
      },
    }),

    ...(isEditMode && {
      editReason: {
        groupId: 'Review',
        required: true,
        multiline: true,
      },
    }),
  };
  return fields as Record<VendorProgramTemplateKeys, FormField>;
};

export const costBasisDefault = {
  CA: 'base_cost',
  MX: 'avg_cost',
  US: 'avg_cost',
  ES: 'avg_cost',
  FR: 'avg_cost',
  IT: 'avg_cost',
  UK: 'avg_cost',
  DE: 'avg_cost',
  IA: 'base_cost',
  IN: 'base_cost',
  AU: 'avg_cost',
  NZ: 'avg_cost',
};
export const costBasisOptions = {
  CA: ['base_cost'],
  MX: ['avg_cost', 'base_cost'],
  US: ['avg_cost'],
  ES: ['avg_cost', 'cp_base_cost'],
  FR: ['avg_cost', 'cp_base_cost'],
  IT: ['avg_cost', 'cp_base_cost'],
  UK: ['avg_cost', 'cp_base_cost'],
  DE: ['avg_cost', 'cp_base_cost'],
  IA: ['base_cost'],
  IN: ['base_cost'],
  AU: ['avg_cost'],
  NZ: ['avg_cost'],
};

export const costBasisOverrideDefault = {
  CA: 'BASE_COST',
  MX: 'AVG_COST',
  US: 'AVG_COST',
  ES: 'AVG_COST',
  FR: 'AVG_COST',
  IT: 'AVG_COST',
  UK: 'AVG_COST',
  DE: 'AVG_COST',
  IA: 'BASE_COST',
  IN: 'BASE_COST',
  AU: 'AVG_COST',
  NZ: 'AVG_COST',
};
export const costBasisOverrideOptions = {
  CA: ['BASE_COST', 'REPLACEMENT_COST'],
  MX: ['AVG_COST', 'BASE_COST', 'REPLACEMENT_COST'],
  US: ['AVG_COST', 'REPLACEMENT_COST'],
  ES: ['AVG_COST', 'CP_BASE_COST', 'REPLACEMENT_COST'],
  FR: ['AVG_COST', 'CP_BASE_COST', 'REPLACEMENT_COST'],
  IT: ['AVG_COST', 'CP_BASE_COST', 'REPLACEMENT_COST'],
  UK: ['AVG_COST', 'CP_BASE_COST', 'REPLACEMENT_COST'],
  DE: ['AVG_COST', 'CP_BASE_COST', 'REPLACEMENT_COST'],
  IA: ['BASE_COST', 'REPLACEMENT_COST'],
  IN: ['BASE_COST', 'REPLACEMENT_COST'],
  AU: ['AVG_COST', 'REPLACEMENT_COST'],
  NZ: ['AVG_COST', 'REPLACEMENT_COST'],
};

export type InclusionExclusionInfo = {
  customer_group_id: string;
  customergroupname: string;
  description: string;
  grouptype: string;
}[];

export type VendorProgramTemplateData = Partial<
  Record<VendorProgramTemplateKeys, FromFieldValue>
>;

const VendorProgramTemplateAtom = atom<VendorProgramTemplateData | undefined>({
  default: undefined,
  key: 'vendor-program-template',
});

const VendorProgramTemplateUpdatedAtom = atom<boolean>({
  default: false,
  key: 'vendor-program-template-updated',
});

export function useVendorProgramTemplateUpdated() {
  return useRecoilState(VendorProgramTemplateUpdatedAtom);
}

export function useVendorProgramSetTemplate() {
  const _setFormData = useSetRecoilState(VendorProgramTemplateAtom);

  return (formData: Parameters<typeof _setFormData>[0]) => {
    _setFormData(formData);
  };
}

export function useVendorProgramTemplate(
  intialValue?: ProgramDetailLoadResponse
) {
  const [fromData, _setFormData] = useRecoilState(VendorProgramTemplateAtom);
  const setIsTemplateUpdated = useSetRecoilState(
    VendorProgramTemplateUpdatedAtom
  );
  const setFormData = (_formData: Parameters<typeof _setFormData>[0]) => {
    _setFormData(_formData);
    setIsTemplateUpdated(!isEqual(fromData, _formData));
  };

  useEffect(() => {
    if (!intialValue) return;
    const data: VendorProgramTemplateData = {
      ...intialValue.manualTemplatesFieldProperties,
      editReason: '',
    };
    data.vendorprogramsname =
      intialValue.vendorProgramDetail?.vendorprogramsname;
    data.businessType = intialValue.vendorProgramDetail?.businessType;
    data.vendorprogramsdescription =
      intialValue.vendorProgramDetail?.vendorprogramsdescription;
    data.vendorcode = intialValue.vendorProgramDetail?.vendorcode;
    data.customergroupinfo = intialValue.vendorProgramDetail?.customergroupinfo;
    data.source = intialValue.manualTemplates?.source;
    data.endUserAddressMatchPercentage =
      intialValue.vendorProgramDetail?.endUserAddressMatchPercentage ?? '0';
    data.isExactEnduserNameMatchRequired =
      intialValue.vendorProgramDetail?.isExactEnduserNameMatchRequired;
    data.uniqueEnduserResellerForClaims =
      intialValue.manualTemplatesFieldProperties?.uniqueEnduserResellerForClaims;
    data.enableCustomOnDemandQuote =
      intialValue.manualTemplatesFieldProperties?.enableCustomOnDemandQuote ??
      DefaultFormData.enableCustomOnDemandQuote;
    data.msrpSource = intialValue.manualTemplatesFieldProperties?.msrpSource;
    data.completeShipment =
      intialValue.manualTemplatesFieldProperties?.completeShipment ??
      DefaultFormData.completeShipment;
    data.status = intialValue.vendorProgramDetail?.status;
    _setFormData(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intialValue]);
  return [fromData ?? {}, setFormData] as const;
}

const isValidForMXCostBasis = (
  discountType:
    | string
    | number
    | boolean
    | InclusionExclusionInfo
    | null
    | undefined
): boolean => {
  if (typeof discountType !== 'string') return false;
  const VALID_MX_DISCOUNT_TYPES = [
    'pct_off_avg',
    'pct_off_list',
    'pct_off_repl',
    'pct_off_base_cost',
    'bdf',
  ];
  return VALID_MX_DISCOUNT_TYPES.includes(discountType);
};

const isMsrpSystemOnlyDiscountType = (
  discountType: FromFieldValue
): boolean => {
  if (typeof discountType !== 'string') return false;
  return ['pct_off_list', 'pct_of_list'].includes(discountType);
};

const isClaimNameActive = (claimName?: string): boolean =>
  claimName === '' || claimName === undefined || claimName === null;

const isBidCostAvgCostOption = (value: FromFieldValue): boolean =>
  value === 'No_increase_cost' || value === 'No_zero_delta';

export const useHandleLogicToRenderFields = (
  isEditMode?: boolean,
  isCloneProgram?: boolean,
  programDetail?: ProgramDetailLoadResponse
) => {
  const [templateInfo] = useVendorProgramTemplate();
  const getAccountNumbersList = useGetAccountNumbersList();
  const [searchParams] = useSearchParams();
  const countryCode = searchParams.get('countryCode');
  const defaultClaimName =
    programDetail?.manualTemplatesFieldProperties?.claimName;

  const handleFieldsLogic = (
    key: VendorProgramTemplateKeys,
    field: FormField
  ) => {
    switch (key) {
      case 'vendorprogramsname':
      case 'nonorderlevel':
        field.disabled = isCloneProgram ? false : isEditMode;
        break;
      case 'endUserAddressMatchPercentage':
        field.disabled =
          templateInfo['isExactEnduserNameMatchRequired'] === 'N';
        break;
      case 'enableQuoteQuantityEdit':
        field.disabled =
          templateInfo['enableCustomOnDemandQuote'] !== 'Y' ||
          templateInfo['isPartialOrderingAllowed'] !== true;
        break;
      case 'customerIdGenerated':
        field.disabled = true;
        break;
      case 'sharedQuantityflag':
        field.disabled = templateInfo['isMultiReseller'] === 'N';
        break;
      case 'resellerAuthorization':
        field.disabled = true;
        break;
      case 'poccFlagging':
        field.disabled = templateInfo['impulseReverseAcop'] === 'Y';
        break;
      case 'impulseReverseAcop':
        field.disabled = templateInfo['poccFlagging'] === 'Y';
        break;
      case 'claimName':
        field.disabled = !isClaimNameActive(defaultClaimName);
        break;
      case 'claimAccountNumberKey':
        field.options = field.options?.concat(
          getAccountNumbersList.data
            ? getAccountNumbersList.data?.map((a) => ({
                label: `${a.account_nbr} - ${a.account_name}`,
                value: a.sk_account,
              }))
            : []
        );
        break;
      case 'costBasis':
        if (countryCode === 'MX') {
          field.disabled = !isValidForMXCostBasis(templateInfo['discountType']);
        }
        break;
    }

    return field;
  };
  return handleFieldsLogic;
};

export const useHandleFieldValueChange = () => {
  const [templateInfo, setTemplateInfo] = useVendorProgramTemplate();
  const setTemplateError = useVendorProgramTemplateError()[1];
  const validateTemplateField = useValidateTemplateField();
  const [searchParams] = useSearchParams();
  const countryCode = searchParams.get('countryCode');

  const handleFieldChange = (
    fieldKey: VendorProgramTemplateKeys,
    field: FormField,
    value: FromFieldValue
  ) => {
    if (field.options?.some((opt) => typeof opt.value === 'boolean')) {
      if (value === 'true') value = true;
      else if (value === 'false') value = false;
    }
    const _templateInfo = { ...templateInfo, [fieldKey]: value };
    const fieldError = validateTemplateField(_templateInfo, fieldKey, {
      [fieldKey]: field,
    } as Record<VendorProgramTemplateKeys, FormField>);
    setTemplateError((e) => {
      return {
        ...e,
        ...fieldError,
      };
    });

    const isAlphaOnlyField =
      fieldKey === 'companyCode' || fieldKey === 'currencyGlobal';
    if (isAlphaOnlyField && typeof value === 'string') {
      value = value.toUpperCase();
    }

    if (isAlphaOnlyField && typeof value === 'string') {
      if (!ALPHA_ONLY_REGEX.test(value)) {
        return;
      }
    }

    if (field.type === 'number') {
      setTemplateInfo((data) => ({
        ...data,
        [fieldKey]: /^$|^\d+$/.test(value as string)
          ? value
          : data?.[fieldKey] ?? '',
      }));
      return;
    }
    if (field.type === 'percentage') {
      setTemplateInfo((data) => ({
        ...data,
        [fieldKey]: /^\d*\.?\d*$/.test(value as string)
          ? value
          : data?.[fieldKey] ?? '',
      }));
      return;
    }
    if (field.type === 'float') {
      setTemplateInfo((data) => ({
        ...data,
        [fieldKey]: /^$|^\d*\.?\d*$/.test(value as string)
          ? value
          : data?.[fieldKey] ?? '',
      }));
      return;
    }
    if (value !== ' ') {
      setTemplateInfo((data) => ({
        ...data,
        [fieldKey]: value,
      }));
    }
    if (fieldKey === 'bcnLinkingLevel') {
      if (value === 'Program') {
        setTemplateInfo((data) => ({
          ...data,
          resellerAuthorization: 'N',
        }));
      }
      if (value === 'Deal') {
        setTemplateInfo((data) => ({
          ...data,
          resellerAuthorization: 'Y',
        }));
      }
      return;
    }
    if (countryCode === 'MX' && fieldKey === 'discountType') {
      setTemplateInfo((data) => ({
        ...data,
        costBasis:
          costBasisDefault[countryCode as keyof typeof costBasisDefault],
        msrpSource: isMsrpSystemOnlyDiscountType(value)
          ? 'System'
          : data?.msrpSource ?? DefaultFormData.msrpSource,
      }));
      return;
    }
    if (fieldKey === 'discountType' && isMsrpSystemOnlyDiscountType(value)) {
      setTemplateInfo((data) => ({
        ...data,
        msrpSource: 'System',
      }));
      return;
    }
    if (fieldKey === 'isMultiReseller' || fieldKey === 'sharedQuantityflag') {
      const nextIsMultiReseller =
        fieldKey === 'isMultiReseller'
          ? value
          : templateInfo['isMultiReseller'];

      let nextSharedQuantityflag =
        fieldKey === 'sharedQuantityflag'
          ? value
          : templateInfo['sharedQuantityflag'];

      if (nextIsMultiReseller === 'N') {
        nextSharedQuantityflag = 'N';
      }

      const customerIdGenerated =
        nextIsMultiReseller === 'N' ||
        (['Y', 'AV'].includes(nextIsMultiReseller as string) &&
          nextSharedQuantityflag === 'Y')
          ? 'N'
          : 'Y';

      setTemplateInfo((data) => ({
        ...data,
        sharedQuantityflag: nextSharedQuantityflag,
        customerIdGenerated,
      }));
      return;
    }

    if (fieldKey === 'isExactEnduserNameMatchRequired' && value === 'N') {
      setTemplateInfo((data) => ({
        ...data,
        endUserAddressMatchPercentage: '0',
      }));
      return;
    }
    if (fieldKey === 'enableCustomOnDemandQuote' && value === 'N') {
      setTemplateInfo((data) => ({
        ...data,
        enableQuoteQuantityEdit: 'N',
      }));
      return;
    }
    if (fieldKey === 'poccFlagging' && value === 'Y') {
      setTemplateInfo((data) => ({
        ...data,
        averageCostValidation: isBidCostAvgCostOption(
          data?.averageCostValidation
        )
          ? DefaultFormData.averageCostValidation
          : data?.averageCostValidation,
        impulseReverseAcop: 'N',
      }));
      return;
    }
    if (fieldKey === 'impulseReverseAcop' && value === 'Y') {
      setTemplateInfo((data) => ({
        ...data,
        averageCostValidation: isBidCostAvgCostOption(
          data?.averageCostValidation
        )
          ? DefaultFormData.averageCostValidation
          : data?.averageCostValidation,
        poccFlagging: 'N',
      }));
      return;
    }
    if (
      fieldKey === 'impulseReverseAcop' &&
      !['B', 'R'].includes(value as string)
    ) {
      setTemplateInfo((data) => ({
        ...data,
        averageCostValidation: isBidCostAvgCostOption(
          data?.averageCostValidation
        )
          ? DefaultFormData.averageCostValidation
          : data?.averageCostValidation,
      }));
      return;
    }
    if (fieldKey === 'costBasis') {
      setTemplateInfo((data) => ({
        ...data,
        acopTypeOverride:
          value === 'cp_base_cost'
            ? 'DELTA'
            : DefaultFormData?.acopTypeOverride,
      }));
    }
  };
  return handleFieldChange;
};

export type VendorProgramTemplateProperties = {
  acopGracePeriod: string | number;
  acopTypeOverride: true;
  autoQuote: string;
  averageCostValidation: string;
  bcnLinkingLevel: string;
  bidRequired: string;
  bidType: string;
  businessType: string;
  claimAccountNumberKey: string;
  claimName: string;
  coG_Approval: string;
  companyCode: string;
  completeShipment: boolean;
  costAdjustmentType: string;
  costBasis: string;
  costBasisOverride: string;
  currencyGlobal: string;
  customerIdGenerated: string;
  customergroupinfo: string;
  discountType: string;
  discountVisible: string;
  editReason: string;
  enableCustomOnDemandQuote: string;
  enableExpiryDateAttributeChange: boolean;
  enableProductFamilyDiscounts: boolean;
  enableQuoteQuantityEdit: string;
  enableRulesEngine: boolean;
  enableSequencing: boolean;
  endUserAddressMatchPercentage: string;
  euMandatoryForClaims: string;
  expiringSoon: string;
  fundUsageAmount: string;
  glAccount: string;
  impulseAcop: string;
  impulseCommentLine1: string;
  impulseCommentLine2: string;
  impulseCompetitor: string;
  impulseComponentFlag: string;
  impulseDebitEnOPercentage: string;
  impulseDebitMDFPercentage: string;
  impulseDebitOTHPercentage: string;
  impulseDebitVRFPPercentage: string;
  impulseDebitVRFVPercentage: string;
  impulseDebitVendorPercentage: string;
  impulseFaxFlag: string;
  impulseHardCopyFlag: string;
  impulseMinimumAcopType: string;
  impulsePROMOFLAG: string;
  impulsePrintCommentsFlag: string;
  impulseProgramName: string;
  impulseReverseAcop: string;
  ingramCostBasis: string;
  isDealSplitAllowedByVendorDiscountType: boolean;
  isExactEnduserNameMatchRequired: string;
  isMapAllIMSku: boolean;
  isMultiReseller: string;
  isPartialOrderingAllowed: boolean;
  isProgramEligibleForFreeProducts: boolean;
  isSplitAndRemoveAllowed: boolean;
  isaddressmatchrequired: string;
  msrpSource: string;
  nonorderlevel: string;
  orderCostOverride: boolean;
  orderReCostRePrice: string;
  orderType: string;
  poccFlagging: string;
  productIncExc: string;
  productLocalizationForWeb: string;
  quantityTracking: string;
  resellerAuthorization: string;
  selectableDiscountLogic: string;
  sharedQuantityflag: string;
  skipAutoQuoteForSameVersion: boolean;
  source: string;
  status?: string;
  suggestedResellerPriceRequired: boolean;
  thresholdCount: string;
  uniqueEnduserResellerForClaims: boolean;
  vendorACOPDiscountType: string;
  vendorDiscountHold: string;
  vendorcode: string;
  vendorprogramsdescription: string;
  vendorprogramsname: string;
  versionNumberGenerated: string;
  visibleonWeb: string;
};

export const useGetTemplateFieldValue = () => {
  const translate = useTranslate();
  const getAccountNumbersList = useGetAccountNumbersList();
  return ({
    field,
    fieldKey,
    value,
  }: {
    field: FormField;
    fieldKey: VendorProgramTemplateKeys;
    value: VendorProgramTemplateData[VendorProgramTemplateKeys];
  }) => {
    const optionsLabel = field.options?.find((o) => o.value === value)?.label;
    if (optionsLabel) {
      return field?.options?.find((x: any) => x.value === value)?.label;
    }
    if (fieldKey === 'customergroupinfo') {
      const groupInfo = value as InclusionExclusionInfo;
      if (groupInfo?.[0]) {
        return `${groupInfo[0].grouptype} ${groupInfo.length} ${translate(
          'CustomerGroups.manageCustomerGroupsNotEmpty'
        )}`;
      }
    }
    if (fieldKey === 'claimAccountNumberKey') {
      const claimAccountDetails = getAccountNumbersList?.data?.find(
        (acc) => acc.sk_account === value
      );
      return claimAccountDetails
        ? `${claimAccountDetails.account_nbr} - ${claimAccountDetails.account_name}`
        : value;
    }
    return value;
  };
};
